import { useState } from 'react';
import { clsx } from 'clsx';
import { useTranslation } from 'react-i18next';

import { Divider, Button } from '@treezor/design-system';

import { useAccesControl } from '@/hooks/useAccessControl';
import { useAppSelector } from '@/hooks/redux-helpers';

import { selectCurrentProfileCardByUserId, selectProfileCardsByUserId } from '@/store/selectors';

import { useProfileContext } from '@/features/context/ProfileContext';
import { PaymentCardsSkeleton } from '@/features/cards/components/PaymentCardsSkeleton';
import { PaymentCardDetails } from '@/features/cards/components/CardDetails/PaymentCardDetails';
import { featuresSlugs } from '@/features/roles/store/models/features.model';
import { CardUnitCreation } from '@/features/cards/components/CardUnitCreation';
import { CardActions } from '@/features/cards/components/CardActions';
import { CardsSlider } from '@/features/cards/components/CardsSlider';

const PaymentCards = ({ canCreateCard }: { canCreateCard: boolean }) => {
  const { t } = useTranslation();
  const { user } = useProfileContext();
  const [shouldShowCardDetails, setShowCardDetails] = useState(false);
  const { hasAccessToFeature } = useAccesControl();

  const isLoading = useAppSelector((state) => selectProfileCardsByUserId(state, user.userId)?.isLoading);
  const paymentCard = useAppSelector((state) => selectCurrentProfileCardByUserId(state, user.userId)?.state?.card);

  const cardContainerClassName = 'box-border border border-box border-primary rounded-200 bg-secondary';

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between">
        <h5 className="typo-h5-bold text-secondary">{t(`pages.currentProfiles.cards.title`)}</h5>
        {canCreateCard && <CardUnitCreation />}
      </div>
      {isLoading ? (
        <PaymentCardsSkeleton />
      ) : (
        <div className={clsx('relative min-w-[25rem]', cardContainerClassName)}>
          <div className="overflow-visible flex flex-col gap-5 my-6">
            {hasAccessToFeature([featuresSlugs.PROFILES_BOARD, featuresSlugs.EDIT_CARDS]) && paymentCard && (
              <CardActions paymentCard={paymentCard} />
            )}
            <CardsSlider />
            <Divider />

            <div className="flex items-center justify-end mx-6">
              <Button
                icon="arrow_forward"
                iconPosition="right"
                variant="ghost"
                onClick={() => setShowCardDetails(true)}
              >
                {t(`pages.currentProfiles.cards.settings.options.details.title`)}
              </Button>
            </div>
          </div>
          <div
            className={clsx(
              'absolute w-full left-0 transition-all duration-300 ease-in-out',
              shouldShowCardDetails ? 'top-0 h-full z-10' : 'top-[calc(100%-64px)] h-0 z-[-1]',
              cardContainerClassName,
            )}
          >
            <PaymentCardDetails onClose={() => setShowCardDetails(false)} />
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentCards;
